/**
 * The AI's manoeuvre vocabulary.
 *
 * Red Baron's own AI is manoeuvre-driven — `RB.EXE`'s string dump carries the
 * stunt names **DIVE, LOOP, RETOURN, SIDESLIP, BAR_ROLL, HALFLOOP, SLIPTURN,
 * ZOOM, IMMEL, SPLIT_S** (docs/game/flight-model.md §2), `SDEF.DAT` holds ten
 * manoeuvre definitions, and `EVADE.DAT` is a weight table that selects between
 * them. This port keeps that shape: a decision layer picks a manoeuvre, and the
 * manoeuvre is an *input script* over the same `FlightInputs` the player's
 * keyboard produces.
 *
 * Each entry is direction-parameterised (`direction` −1 left / +1 right) rather
 * than duplicated left and right, because that is how the `SDEF.DAT` records
 * read: ids 1/2 and 4/5 are the same parameters with the horizontal bound
 * mirrored.
 */

import type { FlightInputs } from "../flight/inputs.ts";
import type { SimState } from "../flight/state.ts";
import type { Tunables } from "../flight/tunables.ts";
import type { CombatBody, RelativeGeometry } from "./geometry.ts";
import {
  barrelRollScript,
  breakTurnScript,
  diveScript,
  halfLoopScript,
  immelmannScript,
  loopScript,
  reverseScript,
  sideslipScript,
  slipTurnScript,
  splitSScript,
  zoomScript,
} from "./maneuver-scripts.ts";
import { extendScript, leadTurnScript, pursueScript } from "./pursuit.ts";

export const MANEUVER_IDS = [
  "pursue",
  "leadTurn",
  "extend",
  "dive",
  "zoom",
  "loop",
  "halfLoop",
  "immelmann",
  "splitS",
  "breakTurn",
  "slipTurn",
  "barrelRoll",
  "sideslip",
  "reverse",
] as const;

export type ManeuverId = (typeof MANEUVER_IDS)[number];

export interface ManeuverCommand {
  readonly id: ManeuverId;
  /** −1 left, +1 right. Ignored by the symmetric manoeuvres. */
  readonly direction: number;
}

export interface ManeuverContext {
  readonly sim: SimState;
  readonly command: ManeuverCommand;
  readonly elapsedSeconds: number;
  readonly target: CombatBody | null;
  readonly geometry: RelativeGeometry | null;
  /** 0..1 — how much of the intercept lead this pilot actually applies. */
  readonly leadQuality: number;
  /**
   * This pilot's standing aiming mistake, radians, in the body frame: the
   * tracking laws fly the nose onto where the pilot *thinks* the solution is.
   * Held for the length of a manoeuvre (`ai-pilot.ts`) so it reads as a settled
   * error and not as jitter.
   */
  readonly aimErrorAzRad: number;
  readonly aimErrorElRad: number;
  readonly tunables: Tunables;
}

export interface ManeuverDescriptor {
  readonly id: ManeuverId;
  /** How long the AI commits to it before the decision layer may pick again. */
  readonly durationSeconds: number;
  /** True when the script needs a target to be meaningful. */
  readonly needsTarget: boolean;
  /** The original's own name for it, where `RB.EXE` has one. */
  readonly originalName: string | null;
  readonly script: (context: ManeuverContext) => FlightInputs;
}

/**
 * Durations are CALIBRATION-PENDING: they set how long a stunt commits the AI,
 * which is a feel knob, not a data point. The `originalName` column is the
 * verbatim `RB.EXE` stunt string where one exists; `null` marks the three
 * port-side additions the original's list has no name for (pure pursuit, a lead
 * turn, and running away).
 */
export const MANEUVERS: Readonly<Record<ManeuverId, ManeuverDescriptor>> = {
  pursue: { id: "pursue", durationSeconds: 1.2, needsTarget: true, originalName: null, script: pursueScript },
  leadTurn: { id: "leadTurn", durationSeconds: 1.2, needsTarget: true, originalName: null, script: leadTurnScript },
  extend: { id: "extend", durationSeconds: 4, needsTarget: false, originalName: null, script: extendScript },
  dive: { id: "dive", durationSeconds: 3.5, needsTarget: false, originalName: "DIVE", script: diveScript },
  zoom: { id: "zoom", durationSeconds: 3.5, needsTarget: false, originalName: "ZOOM", script: zoomScript },
  loop: { id: "loop", durationSeconds: 6, needsTarget: false, originalName: "LOOP", script: loopScript },
  halfLoop: { id: "halfLoop", durationSeconds: 4, needsTarget: false, originalName: "HALFLOOP", script: halfLoopScript },
  immelmann: { id: "immelmann", durationSeconds: 5, needsTarget: false, originalName: "IMMEL", script: immelmannScript },
  splitS: { id: "splitS", durationSeconds: 4.5, needsTarget: false, originalName: "SPLIT_S", script: splitSScript },
  breakTurn: { id: "breakTurn", durationSeconds: 3, needsTarget: false, originalName: null, script: breakTurnScript },
  slipTurn: { id: "slipTurn", durationSeconds: 3, needsTarget: false, originalName: "SLIPTURN", script: slipTurnScript },
  barrelRoll: { id: "barrelRoll", durationSeconds: 2.5, needsTarget: false, originalName: "BAR_ROLL", script: barrelRollScript },
  sideslip: { id: "sideslip", durationSeconds: 2, needsTarget: false, originalName: "SIDESLIP", script: sideslipScript },
  reverse: { id: "reverse", durationSeconds: 3.5, needsTarget: false, originalName: "RETOURN", script: reverseScript },
};

/** The manoeuvre names `RB.EXE` itself carries, for the fidelity test. */
export const ORIGINAL_STUNT_NAMES: readonly string[] = [
  "DIVE",
  "LOOP",
  "RETOURN",
  "SIDESLIP",
  "BAR_ROLL",
  "HALFLOOP",
  "SLIPTURN",
  "ZOOM",
  "IMMEL",
  "SPLIT_S",
];

export function maneuverInputs(context: ManeuverContext): FlightInputs {
  return MANEUVERS[context.command.id].script(context);
}

export function maneuverDurationSeconds(id: ManeuverId): number {
  return MANEUVERS[id].durationSeconds;
}
